/**
 * ProductsPage — HouseholdProduct catalog.
 *
 * Lists the household's HouseholdProducts, supports search, create, and delete.
 * Linked-offer browsing is represented as `linked_offer_count` on the row
 * — a dedicated offer-link editor belongs in a drill-in screen (future
 * task). For T8.3 we keep the surface bounded to the catalog CRUD so the
 * orchestrator path (household-products-orchestrator) is exercised end-to-end.
 *
 * Reads: `useHouseholdProducts` (or `useSearchHouseholdProducts` when a query is typed).
 * Writes: `useOrchestrators().HouseholdProducts` — orchestrator handles reducer +
 * storage + API per the T6 contract.
 *
 * Ported in spirit from legacy `SettingsHouseholdProducts.tsx`. CP5: token refs
 * only.
 */

import { useState, type CSSProperties, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useMe,
  useHouseholdProducts,
  useSearchHouseholdProducts,
} from '@shopista/shared/api/hooks';
import { apiErrorMessage } from '@shopista/shared/api';
import type { HouseholdProductWithDetails } from '@shopista/shared/types';
import { formatResolvedMeasurement } from '@shopista/shared/types';
import {
  Button,
  Card,
  Input,
  List,
  ListItem,
  Modal,
  Spinner,
} from '../../components';
import { useOrchestrators } from '../../stores/orchestrator-context';
import { randomUuid } from '../../lib/randomUuid';
import { usePublishLayoverTitle } from '../../stores/useLayoverTitle';
import { routes } from '../../routes/paths';

// The products surface's name — shown both as the in-content heading and (via
// the layover-title publish) in the app header center. One source so they match.
const PRODUCTS_TITLE = 'Household products';

const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-4)',
};

const headerRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
};

const searchRowStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3)',
  alignItems: 'flex-end',
};

const errorStyle: CSSProperties = {
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-sm)',
};

const emptyStyle: CSSProperties = {
  padding: 'var(--space-6)',
  textAlign: 'center',
  color: 'var(--color-text-muted)',
};

const metaStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
};

export function ProductsPage(): JSX.Element {
  const [query, setQuery] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const allQuery = useHouseholdProducts();
  const searchQuery = useSearchHouseholdProducts(query);
  const isSearching = query.length > 0;
  const activeQuery = isSearching ? searchQuery : allQuery;
  const products = activeQuery.data?.products ?? [];

  const orchestrators = useOrchestrators();
  const navigate = useNavigate();

  // The products layover isn't a SettingsLayover surface, so it publishes its own
  // title to the app header center while mounted.
  usePublishLayoverTitle(PRODUCTS_TITLE);

  const handleDelete = async (product: HouseholdProductWithDetails) => {
    setActionError(null);
    try {
      await orchestrators.HouseholdProducts.handleRemoveProduct({
        productId: product.id,
      });
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Failed to delete product'));
    }
  };

  return (
    <div style={pageStyle} data-testid="products-page">
      <div style={headerRowStyle}>
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>
          {PRODUCTS_TITLE}
        </h2>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setAddOpen(true)}
          data-testid="products-add-open"
        >
          + New Product
        </Button>
      </div>

      <div style={searchRowStyle}>
        <div style={{ flex: 1 }}>
          <Input
            label="Search"
            placeholder="Filter by name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="products-search"
          />
        </div>
        {query && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setQuery('')}
            data-testid="products-search-clear"
          >
            Clear
          </Button>
        )}
      </div>

      {actionError && (
        <div role="alert" style={errorStyle} data-testid="products-action-error">
          {actionError}
        </div>
      )}

      {activeQuery.isLoading ? (
        <div style={emptyStyle} data-testid="products-loading">
          <Spinner />
        </div>
      ) : activeQuery.isError ? (
        <Card padding="lg" data-testid="products-error">
          <p>Could not load your products.</p>
          <p style={errorStyle}>
            {activeQuery.error instanceof Error
              ? activeQuery.error.message
              : String(activeQuery.error)}
          </p>
        </Card>
      ) : products.length === 0 ? (
        <Card padding="lg" data-testid="products-empty">
          <div style={emptyStyle}>
            {isSearching
              ? 'No products match that search.'
              : 'You have no products yet. Add one to get started.'}
          </div>
        </Card>
      ) : (
        <Card padding="none" shadow="sm">
          <List data-testid="products-list">
            {products.map((product, index) => (
              <ProductRow
                key={product.id}
                product={product}
                divided={index > 0}
                onOpen={() => navigate(`${routes.products}/${product.id}`)}
                onDelete={() => handleDelete(product)}
              />
            ))}
          </List>
        </Card>
      )}

      <CreateProductModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => setAddOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function ProductRow({
  product,
  divided,
  onOpen,
  onDelete,
}: {
  product: HouseholdProductWithDetails;
  divided: boolean;
  onOpen: () => void;
  onDelete: () => void;
}): JSX.Element {
  const measurementText = formatResolvedMeasurement(product);
  return (
    <ListItem
      divided={divided}
      data-testid={`products-item-${product.id}`}
      trailing={
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          aria-label={`Delete ${product.display_name}`}
          data-testid={`products-item-${product.id}-delete`}
        >
          Delete
        </Button>
      }
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Edit ${product.display_name}`}
        data-testid={`products-item-${product.id}-open`}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
          textAlign: 'left',
          cursor: 'pointer',
          color: 'inherit',
          font: 'inherit',
          width: '100%',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-medium)',
          }}
        >
          {product.display_name}
        </div>
        <div style={metaStyle}>
          {product.category_name ? `${product.category_name} · ` : ''}
          {/* T36.3 — resolved measurement via the ONE shared formatter (`4 L`,
              `12 × 20 g`). Omitted when the product has no resolved measurement. */}
          {measurementText ? `${measurementText} · ` : ''}
          {product.linked_offer_count} linked offer
          {product.linked_offer_count === 1 ? '' : 's'}
          {product.default_offer_price != null
            ? ` · $${product.default_offer_price.toFixed(2)}`
            : ''}
        </div>
      </button>
    </ListItem>
  );
}

// ---------------------------------------------------------------------------
// Create modal
// ---------------------------------------------------------------------------

function CreateProductModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const [displayName, setDisplayName] = useState('');
  const [defaultQuantityText, setDefaultQuantityText] = useState('1');
  const [notes, setNotes] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const orchestrators = useOrchestrators();

  // Household id comes from the currently-signed-in session. `useMe` is the
  // cheapest source — the AppShell already keeps this fresh, so this is a
  // cache hit in practice.
  const me = useMe();
  const householdId = me.data?.householdId;

  const reset = () => {
    setDisplayName('');
    setDefaultQuantityText('1');
    setNotes('');
    setLocalError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLocalError(null);
    if (!displayName.trim()) {
      setLocalError('Name is required.');
      return;
    }
    const defaultQuantity = Number(defaultQuantityText);
    if (!Number.isFinite(defaultQuantity) || defaultQuantity <= 0) {
      setLocalError('Default quantity must be a positive number.');
      return;
    }
    if (!householdId) {
      setLocalError('No household in session. Sign in again.');
      return;
    }

    setSubmitting(true);
    const nowIso = new Date().toISOString();
    const optimistic: HouseholdProductWithDetails = {
      id: `optimistic-${randomUuid()}`,
      household_id: householdId,
      display_name: displayName.trim(),
      category_id: null,
      notes: notes.trim() || null,
      tags: [],
      image_url: null,
      default_quantity: defaultQuantity,
      retailer_preferences: [],
      allow_substitutions: true,
      substitution_notes: null,
      always_keep_stocked: false,
      expected_price_value: null,
      expected_price_currency: 'CAD',
      expected_price_notes: null,
      seed_householdproduct_id: null,
      seeded_at: null,
      created_by: null,
      created_at: nowIso,
      updated_at: nowIso,
      category_name: null,
      linked_offer_count: 0,
      favorite_offer_count: 0,
      default_offer_id: null,
      default_offer_title: null,
      default_offer_price: null,
      default_store_name: null,
    };

    const trimmedNotes = notes.trim();
    const request = {
      displayName: displayName.trim(),
      defaultQuantity,
      ...(trimmedNotes ? { notes: trimmedNotes } : {}),
    };

    try {
      await orchestrators.HouseholdProducts.handleAddProduct({
        optimistic,
        request,
      });
      reset();
      onCreated();
    } catch (err) {
      setLocalError(apiErrorMessage(err, 'Failed to create product'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="New product">
      <form onSubmit={handleSubmit} data-testid="products-add-form">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
          }}
        >
          <Input
            label="Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            data-testid="products-add-name"
            autoFocus
          />
          <Input
            label="Default quantity"
            type="number"
            min="0"
            step="1"
            value={defaultQuantityText}
            onChange={(e) => setDefaultQuantityText(e.target.value)}
            data-testid="products-add-default-quantity"
          />
          <Input
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="products-add-notes"
            hint="Optional."
          />

          {localError && (
            <div role="alert" style={errorStyle} data-testid="products-add-error">
              {localError}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              justifyContent: 'flex-end',
              marginTop: 'var(--space-2)',
            }}
          >
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              data-testid="products-add-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={submitting}
              disabled={submitting}
              data-testid="products-add-submit"
            >
              Create
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

